<template>
  <div>
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              <div>
                Search Results
              </div>
            </div>
          </template>
          <template #actions v-if="isDownloadReady">
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <a :href="downloadRecords()">
                    <font-awesome-icon
                      :icon="['far', 'file-download']"
                      class="fa-2x"
                      title="Download Records"
                    />
                  </a>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div v-if="this.searchType === 'session'"  style="padding: 2rem 3rem">
        <es-loader v-if="loadingSession" style="margin: 6.5rem"/>
        <es-exception v-else-if="loadingErrorSession"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="There was an issue. Please contact the admin"
        />
        <es-exception
          v-else-if="!sessionDupQueueResults"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="Search criteria must be entered to view results."
        />
        <es-exception
          v-else-if="!sessionDupQueueResults.length"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="No results were found."
        />
        <div  class="table-container" v-else>
          <table class="table is-fullwidth is-hoverable">
            <es-sorter
              :columnHeaders="columnHeaders"
            />
            <tbody>
              <tr v-for="(record, index) in sessionDupQueueResults" :key="index">
                <td class="weighted-font">
                  {{ record.group.LAST_NAME ? record.group.LAST_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.FIRST_NAME ? record.group.FIRST_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.MIDDLE_NAME ? record.group.MIDDLE_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.DATE_OF_BIRTH ? record.group.DATE_OF_BIRTH : null }}
                </td>
                <td>
                  {{ record.group.GENDER_CODE ? mapGender(record.group.GENDER_CODE) : null }}
                </td>
                <td>
                  {{ record.hasSameSsn === true ? 'Y' : 'N' }}
                </td>
                <td>
                  {{ record.count }}
                </td>
                <td style="text-align: right;">
                  <a @click="viewGroupDetails(record)"
                    @keypress="viewGroupDetails(record)"
                    tabindex="0"
                    :class="{ disabled: record.count > maxDupQueueRecords }">
                    <font-awesome-icon
                      :icon="['far', 'angle-double-right']"
                      class="fa-2x"
                      title="View Details"
                    />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!loadingSession && this.sessionDupQueueResults !== null" class="pager-container">
          <es-pager :totalCount="this.sessionTotalCount"/>
        </div>
      </div>
      <div v-else  style="padding: 2rem 3rem">
        <es-loader v-if="loadingQueue" style="margin: 6.5rem"/>
        <es-exception v-else-if="loadingErrorQueue"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="There was an issue. Please contact the admin"
        />
        <es-exception
          v-else-if="!queueDupQueueResults"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="Search criteria must be entered to view results."
        />
        <es-exception
          v-else-if="!queueDupQueueResults.length"
          style="padding: 6.5rem"
          margin="0rem" size="4x"
          exceptionMessage="No results were found."
        />
        <div class="table-container" v-else>
          <table class="table is-fullwidth is-hoverable">
            <es-sorter
              :columnHeaders="columnHeaders"
            />
            <tbody>
              <tr v-for="(record,index) in queueDupQueueResults" :key="index">
                <td class="weighted-font">
                  {{ record.group.LAST_NAME ? record.group.LAST_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.FIRST_NAME ? record.group.FIRST_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.MIDDLE_NAME ? record.group.MIDDLE_NAME : null }}
                </td>
                <td class="weighted-font">
                  {{ record.group.DATE_OF_BIRTH ? record.group.DATE_OF_BIRTH : null }}
                </td>
                <td>
                  {{ record.group.GENDER_CODE ? mapGender(record.group.GENDER_CODE) : null }}
                </td>
                <td>
                  {{ record.hasSameSsn === true ? 'Y' : 'N' }}
                </td>
                <td>
                  {{ record.count }}
                </td>
                <td style="text-align: right;">
                  <a @click="viewGroupDetails(record)"
                    @keypress="viewGroupDetails(record)"
                    tabindex="0"
                    :class="{ disabled: record.count > maxDupQueueRecords }">
                    <font-awesome-icon
                      :icon="['far', 'angle-double-right']"
                      class="fa-2x"
                      title="View Details"
                    />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!loadingQueue && this.queueDupQueueResults !== null" class="pager-container">
          <es-pager :totalCount="this.queueTotalCount"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faFileDownload,
} from '@fortawesome/pro-regular-svg-icons';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
  faFileDownload,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
  },
  props: {
    dupFields: {
      type: Array,
    },
    loadingQueue: {
      type: Boolean,
    },
    loadingErrorQueue: {
      type: Boolean,
    },
    loadingSession: {
      type: Boolean,
    },
    loadingErrorSession: {
      type: Boolean,
    },
    personTypeQueue: {
      type: String,
    },
    personTypeSession: {
      type: String,
    },
    queueResultsStored: {
      type: Object,
    },
    searchType: {
      type: String,
    },
    sessionFields: {
      type: Array,
    },
    sessionResultsStored: {
      type: Object,
    },
  },
  watch: {
    queueResultsStored() {
      this.queueDupQueueResults = this.queueResultsStored ? this.queueResultsStored.data : null;
      this.queueTotalCount = this.queueResultsStored ? this.queueResultsStored.totalCount : null;
      this.initializePersonType();
      this.initializeColumnHeaders();
    },
    sessionResultsStored() {
      this.sessionDupQueueResults = this.sessionResultsStored
        ? this.sessionResultsStored.data : null;
      this.sessionTotalCount = this.sessionResultsStored
        ? this.sessionResultsStored.totalCount : null;
      this.initializePersonType();
      this.initializeColumnHeaders();
    },
    $route() {
      this.initializePersonType();
      this.initializeColumnHeaders();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    isDownloadReady() {
      if (!this.hasModule('PRSID:IDENT:DOWNLOAD')) {
        return false;
      }

      if (this.searchType === 'queue') {
        return (
          !this.loadingQueue
          && !this.loadingErrorQueue
          && Array.isArray(this.queueDupQueueResults)
          && this.queueDupQueueResults.length > 0
        );
      }

      if (this.searchType === 'session') {
        return (
          !this.loadingSession
          && !this.loadingErrorSession
          && Array.isArray(this.sessionDupQueueResults)
          && this.sessionDupQueueResults.length > 0
        );
      }

      return false;
    },
    maxDupQueueRecords() {
      return this.$store.state.propertiesModule.properties['PERSON-MAX_DUP_QUEUE_RECORDS'];
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      fileNameForDownload: null,
      personType: null,
      queueDupQueueResults: null,
      queueTotalCount: null,
      sessionDupQueueResults: null,
      sessionTotalCount: null,
    };
  },
  created() {
    this.initializePersonType();
    this.initializeColumnHeaders();
  },
  methods: {
    downloadRecords() {
      let url = null;
      if (this.searchType === 'queue') {
        let query = `${axios.defaults.baseURL}idmanager/download?type=dupQueue`;

        if (this.personTypeQueue !== null && this.personTypeQueue !== '') {
          query += `&personType=${this.personTypeQueue}`;
        }

        query += `&fields=${this.dupFields}`;
        url = query;
      }

      if (this.searchType === 'session') {
        let query = `${axios.defaults.baseURL}idmanager/download?type=session`;

        if (this.personTypeSession !== null && this.personTypeSession !== '') {
          query += `&personType=${this.personTypeSession}`;
        }

        const fields = this.sessionFields || JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS')).map(a => a.code);
        query += `&fields=${fields}`;

        url = query;
      }
      return url;
    },
    mapGender(gender) {
      const genderMap = {
        M: 'Male',
        F: 'Female',
      };
      return genderMap[gender] || null;
    },
    viewGroupDetails(data) {
      const updatedData = {
        ...data,
        type: this.searchType,
      };
      this.$emit('groupInfo', updatedData);
    },
    initializeColumnHeaders() {
      if (!this.personType) {
        return;
      }
      this.columnHeaders = [
        {
          header: `${this.getFieldLabel(`PER:${this.personType}:ASSIGN:LAST_NAME`)}`,
          field: 'lastname',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType}:ASSIGN:FIRST_NAME`)}`,
          field: 'firstname',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType}:ASSIGN:MIDDLE_NAME`)}`,
          field: 'middlename',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType}:ASSIGN:DATE_OF_BIRTH`)}`,
          field: 'dob',
          sortable: false,
          order: null,
        },
        {
          header: `${this.getFieldLabel(`PER:${this.personType}:ASSIGN:GENDER_CODE`)}`,
          field: 'gender',
          sortable: false,
          order: null,
        },
        {
          header: 'Same SSN',
          sortable: false,
          order: null,
        },
        {
          header: '# of Records',
          field: 'count',
          sortable: false,
          order: 'ASC',
        },
        {
          header: 'Actions',
          field: 'actions',
          sortable: false,
          order: null,
          style: 'text-align: right; width: 1%; white-space: nowrap;',
        },
      ];
    },
    initializePersonType() {
      if (this.$route.query.search === 'queue') {
        this.personType = this.personTypeQueue;
      }
      if (this.$route.query.search === 'session') {
        this.personType = this.personTypeSession;
      }
      if (this.personType == null) { // All person types selected
        this.personType = this.defaultPersonType;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    max-width: 100%
    white-space: nowrap
  table td
    max-width: 100%
    white-space: nowrap
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .weighted-font
    font-weight: 700 !important
  .table-container
    overflow-x: auto
    margin-bottom: 0
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
